import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Package, AlertTriangle } from 'lucide-react';
import { buildUrl } from '../../config/api';
import '../styles/SendOutStickyNote.css';

/**
 * SendOutStickyNote
 *
 * Draggable sticky note showing today's 'Booked' Send Out appointments for the
 * logged-in worker. Appears on login and re-appears every hour on the hour,
 * but stops if there are no more appointments left for the day.
 *
 * Also shows overdue Send Outs from the past 7 days where aptstatus is not
 * 'Confirmed', grouped separately at the top.
 *
 * Listens to the global 'orderAutoCompleted' window event fired by AutoCompleteButton:
 *   - action 'complete' → removes that order immediately, stops hourly if list is now empty
 *   - action 'undo'     → re-fetches and shows the note again
 */
const SendOutStickyNote = ({ user }) => {
  const [visible, setVisible] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [overdueAppointments, setOverdueAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 400, y: window.innerHeight - 400 });
  const [dragging, setDragging] = useState(false);
  const navigate = useNavigate();
  const dragOffset = useRef({ x: 0, y: 0 });
  const noteRef = useRef(null);
  const hourTimeoutRef = useRef(null);
  const hourIntervalRef = useRef(null);
  const isFetchingRef = useRef(false);
  const lastCountRef = useRef(0);

  const username = user?.UserName || user?.username;

  // ------------------------------------------------------------------
  // Fetch overdue send-outs (past 7 days, aptstatus !== 'Confirmed')
  // ------------------------------------------------------------------
  const fetchOverdueAppointments = useCallback(async () => {
    if (!username) return;
    try {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);

      const fmt = (d) => d.toISOString().split('T')[0];
      const res = await fetch(
        buildUrl(
          `/api/appointments/my-send-outs?username=${encodeURIComponent(username)}&startDate=${fmt(startDate)}&endDate=${fmt(yesterday)}`
        )
      );
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      const appts = (data.appointments || []).filter(
        (a) => a.aptstatus !== 'Confirmed'
      );
      setOverdueAppointments(appts);
    } catch (err) {
      console.error('SendOutStickyNote overdue fetch error:', err);
      setOverdueAppointments([]);
    }
  }, [username]);

  // ------------------------------------------------------------------
  // Fetch today's appointments — returns the fetched appointment count
  // ------------------------------------------------------------------
  const fetchAppointments = useCallback(async () => {
    if (!username) return 0;
    if (isFetchingRef.current) return lastCountRef.current;
    isFetchingRef.current = true;
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(
        buildUrl(`/api/appointments/my-send-outs?username=${encodeURIComponent(username)}&date=${today}`)
      );
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      const appts = data.appointments || [];
      setAppointments(appts);
      fetchOverdueAppointments();
      lastCountRef.current = appts.length;
      return appts.length;
    } catch (err) {
      console.error('SendOutStickyNote fetch error:', err);
      setAppointments([]);
      lastCountRef.current = 0;
      return 0;
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [username, fetchOverdueAppointments]);

  // ------------------------------------------------------------------
  // Clear all hourly timers
  // ------------------------------------------------------------------
  const clearTimers = useCallback(() => {
    if (hourTimeoutRef.current) clearTimeout(hourTimeoutRef.current);
    if (hourIntervalRef.current) clearInterval(hourIntervalRef.current);
  }, []);

  // ------------------------------------------------------------------
  // Schedule re-appearance at the top of every hour.
  // If the hourly fetch returns 0 appointments, the timer stops.
  // ------------------------------------------------------------------
  const scheduleNextHour = useCallback(() => {
    clearTimers();
    const now = new Date();
    const msUntilNextHour =
      (60 - now.getMinutes()) * 60 * 1000 -
      now.getSeconds() * 1000 -
      now.getMilliseconds();

    hourTimeoutRef.current = setTimeout(async () => {
      const count = await fetchAppointments();
      if (count > 0) {
        setVisible(true);
        // After the first scheduled hour, repeat every 60 min
        hourIntervalRef.current = setInterval(async () => {
          const c = await fetchAppointments();
          if (c > 0) {
            setVisible(true);
          } else {
            // No appointments left — stop the hourly recurrence
            clearInterval(hourIntervalRef.current);
          }
        }, 60 * 60 * 1000);
      }
      // count === 0: don't reschedule, let the note stay closed
    }, msUntilNextHour);
  }, [fetchAppointments, clearTimers]);

  // ------------------------------------------------------------------
  // On login / mount: show immediately, start hourly schedule
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!username) return;
    fetchAppointments().then((count) => {
      if (count > 0) setVisible(true);
    });
    fetchOverdueAppointments().then(() => {
      setOverdueAppointments((prev) => {
        if (prev.length > 0) setVisible(true);
        return prev;
      });
    });
    scheduleNextHour();
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  // ------------------------------------------------------------------
  // Listen for auto-complete / undo events from AutoCompleteButton
  // ------------------------------------------------------------------
  useEffect(() => {
    const handleOrderEvent = async (e) => {
      const { orderNo, action } = e.detail;

      if (action === 'complete') {
        // Immediately pull the completed order from both lists
        setAppointments((prev) => {
          const remaining = prev.filter((a) => String(a.orderno) !== String(orderNo));
          if (remaining.length === 0) clearTimers();
          return remaining;
        });
        setOverdueAppointments((prev) =>
          prev.filter((a) => String(a.orderno) !== String(orderNo))
        );
        fetchAppointments();
      } else if (action === 'undo') {
        // Restore flow: re-fetch and show the note again
        const count = await fetchAppointments();
        if (count > 0) {
          setVisible(true);
          scheduleNextHour();
        }
      }
    };

    window.addEventListener('orderAutoCompleted', handleOrderEvent);
    return () => window.removeEventListener('orderAutoCompleted', handleOrderEvent);
  }, [fetchAppointments, clearTimers, scheduleNextHour]);

  // ------------------------------------------------------------------
  // Drag — mouse + touch events
  // ------------------------------------------------------------------
  const startDrag = (clientX, clientY) => {
    setDragging(true);
    dragOffset.current = {
      x: clientX - position.x,
      y: clientY - position.y,
    };
  };

  const handleMouseDown = (e) => {
    if (!e.target.closest('.send-out-sticky-header')) return;
    e.preventDefault();
    startDrag(e.clientX, e.clientY);
  };

  const handleTouchStart = (e) => {
    if (!e.target.closest('.send-out-sticky-header')) return;
    e.preventDefault();
    const t = e.touches[0];
    startDrag(t.clientX, t.clientY);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMouseMove = (e) =>
      setPosition({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    const onTouchMove = (e) => {
      e.preventDefault();
      const t = e.touches[0];
      setPosition({
        x: t.clientX - dragOffset.current.x,
        y: t.clientY - dragOffset.current.y,
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (!visible || !username) return null;

  const todayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  // Deduplicate by orderno — same order should only appear once
  const dedupeByOrder = (list) => {
    const seen = new Set();
    return list.filter((a) => {
      const key = a.orderno ? String(a.orderno) : `noid-${a.ID}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const uniqueAppointments = dedupeByOrder(appointments);
  const uniqueOverdue = dedupeByOrder(overdueAppointments);

  const formatDate = (dt) => {
    if (!dt) return '';
    return new Date(dt).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric',
    });
  };

  const formatTime = (dt) => {
    if (!dt) return '';
    return new Date(dt).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  const AppointmentRow = ({ appt, overdue = false }) => (
    <div className={`send-out-sticky-item${overdue ? ' overdue' : ''}`}>
      <div className="send-out-sticky-item-info">
        <div className="send-out-sticky-client">{appt.ClientName}</div>
        <div className="send-out-sticky-time">
          {overdue ? formatDate(appt.EventStart) : formatTime(appt.EventStart)}
        </div>
      </div>
      {appt.orderno ? (
        <button
          className="send-out-sticky-order-btn"
          onClick={() => navigate(`/work-order-profile/${appt.orderno}`)}
          title={`Go to order #${appt.orderno}`}
        >
          #{appt.orderno}
        </button>
      ) : (
        <span className="send-out-sticky-order-btn no-order">No order</span>
      )}
    </div>
  );

  return (
    <div
      ref={noteRef}
      className={`send-out-sticky${dragging ? ' dragging' : ''}`}
      style={{ left: position.x, top: position.y }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* Header */}
      <div className="send-out-sticky-header">
        <div className="send-out-sticky-title">
          <Package size={16} />
          My Send Outs
          {uniqueOverdue.length > 0 && (
            <span className="send-out-sticky-overdue-badge">
              {uniqueOverdue.length} overdue
            </span>
          )}
        </div>
        <button
          className="send-out-sticky-close"
          onClick={() => setVisible(false)}
          title="Close (reappears next hour if appointments remain)"
        >
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      <div className="send-out-sticky-body">

        {/* Overdue section */}
        {uniqueOverdue.length > 0 && (
          <div className="send-out-sticky-overdue-group">
            <div className="send-out-sticky-group-label overdue-label">
              <AlertTriangle size={12} />
              Overdue Send Outs
            </div>
            <div className="send-out-sticky-list">
              {uniqueOverdue.map((appt) => (
                <AppointmentRow key={appt.ID} appt={appt} overdue />
              ))}
            </div>
          </div>
        )}

        {/* Today section */}
        <div className="send-out-sticky-date">{todayLabel}</div>
        {loading ? (
          <div className="send-out-sticky-loading">Loading…</div>
        ) : uniqueAppointments.length === 0 ? (
          <div className="send-out-sticky-empty">No pending Send Out appointments today.</div>
        ) : (
          <div className="send-out-sticky-list">
            {uniqueAppointments.map((appt) => (
              <AppointmentRow key={appt.ID} appt={appt} />
            ))}
          </div>
        )}
      </div>

      {(uniqueAppointments.length > 0 || uniqueOverdue.length > 0) && (
        <div className="send-out-sticky-footer">
          Click an order to open and complete it.
        </div>
      )}
    </div>
  );
};

export default SendOutStickyNote;
